import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const SiteMap = () => {
  const siteMapSections = [
    {
      title: 'Main Pages',
      icon: 'Home',
      links: [
        { label: 'Homepage', path: '/', description: 'Discover your perfect wedding vendors' },
        { label: 'Browse All Categories', path: '/browse-categories-all-vendor-types', description: 'Explore all vendor categories' },
        { label: 'All Vendors', path: '/all-vendors', description: 'Browse all registered vendors' },
        { label: 'Category Browse', path: '/category-browse', description: 'Browse vendors by specific category' },
        { label: 'Vendor Profile', path: '/vendor-profile', description: 'View detailed vendor information' }
      ]
    },
    {
      title: 'User Features',
      icon: 'Users',
      links: [
        { label: 'User Dashboard', path: '/user-dashboard', description: 'Manage your wedding planning' },
        { label: 'Wedding Planning Tools', path: '/user-dashboard-wedding-planning-tools', description: 'Advanced planning tools and checklists' },
        { label: 'Clean Mobile Dashboard', path: '/user-dashboard-clean-mobile-layout', description: 'Mobile-optimized dashboard' },
        { label: 'Planning Tools', path: '/planning-tools', description: 'Wedding planning utilities' },
        { label: 'Budget Calculator', path: '/planning-tools/budget-calculator', description: 'Calculate your wedding budget' },
        { label: 'Wedding Checklist', path: '/planning-tools/checklist', description: 'Complete wedding planning checklist' }
      ]
    },
    {
      title: 'Vendor Features',
      icon: 'Briefcase',
      links: [
        { label: 'Vendor Registration', path: '/vendor-registration', description: 'Join ShaadiNest as a vendor' },
        { label: 'Vendor Dashboard', path: '/vendor-dashboard', description: 'Manage your vendor profile and bookings' }
      ]
    },
    {
      title: 'Authentication',
      icon: 'Lock',
      links: [
        { label: 'Sign In', path: '/sign-in-page', description: 'Access your account' },
        { label: 'Sign Up', path: '/sign-up-page', description: 'Create a new account' },
        { label: 'Login (Alternative)', path: '/login', description: 'Alternative login page' },
        { label: 'Signup (Alternative)', path: '/signup', description: 'Alternative signup page' }
      ]
    },
    {
      title: 'Company Information',
      icon: 'Building',
      links: [
        { label: 'About Us', path: '/about-us', description: 'Learn about ShaadiNest' },
        { label: 'Contact Us', path: '/contact-us', description: 'Get in touch with our team' },
        { label: 'Careers', path: '/careers', description: 'Join our team' }
      ]
    },
    {
      title: 'Support & Legal',
      icon: 'Shield',
      links: [
        { label: 'Help Center', path: '/help-center', description: 'Find answers and get support' },
        { label: 'Privacy Policy', path: '/privacy-policy', description: 'Our privacy policy and data practices' },
        { label: 'Terms of Service', path: '/terms-of-service', description: 'Terms and conditions of use' }
      ]
    },
    {
      title: 'Admin & System',
      icon: 'Settings',
      links: [
        { label: 'Admin Dashboard', path: '/admin-dashboard', description: 'Administrative controls and analytics' },
        { label: 'Design System', path: '/design-system-update', description: 'UI components and design guidelines' }
      ]
    }
  ];

  const categoryPages = [
    { label: 'Wedding Photography', path: '/category-browse?category=photography' },
    { label: 'Mehndi Artists', path: '/category-browse?category=mehndi' },
    { label: 'Wedding Venues', path: '/category-browse?category=venues' },
    { label: 'Catering Services', path: '/category-browse?category=catering' },
    { label: 'Wedding Decorators', path: '/category-browse?category=decoration' },
    { label: 'Bridal Makeup', path: '/category-browse?category=makeup' },
    { label: 'Wedding Music & DJ', path: '/category-browse?category=music' },
    { label: 'Wedding Planners', path: '/category-browse?category=planners' }
  ];

  return (
    <>
      <Helmet>
        <title>Site Map - ShaadiNest | Wedding Marketplace Navigation</title>
        <meta 
          name="description" 
          content="Navigate ShaadiNest easily with our comprehensive site map. Find all pages including vendor listings, planning tools, dashboards, and support resources." 
        />
        <meta name="keywords" content="sitemap, navigation, wedding vendors, planning tools, dashboards" />
      </Helmet>
      <div className="min-h-screen bg-surface-50">
        <Header />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-primary-600 rounded-2xl mx-auto mb-6 shadow-soft">
              <Icon name="Map" size={32} color="#FFFFFF" strokeWidth={2} />
            </div>
            <h1 className="font-heading font-bold text-4xl text-text-primary mb-4">
              Site Map
            </h1>
            <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
              Navigate ShaadiNest with ease. Find all our pages and features organized by category for quick access to everything you need for your perfect wedding.
            </p>
          </div>

          {/* Main Navigation Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {siteMapSections?.map((section) => (
              <div key={section?.title} className="bg-white rounded-2xl shadow-card border border-border p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-50 rounded-lg">
                    <Icon name={section?.icon} size={20} color="#8B5CF6" />
                  </div>
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    {section?.title}
                  </h2>
                </div>
                
                <div className="space-y-4">
                  {section?.links?.map((link) => (
                    <Link
                      key={link?.path}
                      to={link?.path}
                      className="block group p-3 rounded-lg border border-border hover:border-primary-200 hover:bg-primary-50 transition-all duration-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-body font-medium text-text-primary group-hover:text-primary transition-colors duration-200">
                            {link?.label}
                          </h3>
                          {link?.description && (
                            <p className="font-body text-sm text-text-secondary mt-1">
                              {link?.description}
                            </p>
                          )}
                        </div>
                        <Icon 
                          name="ArrowRight" 
                          size={16} 
                          className="text-text-secondary group-hover:text-primary transition-colors duration-200 mt-1 ml-2" 
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Category Pages Section */}
          <div className="bg-white rounded-2xl shadow-card border border-border p-6 mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-secondary-50 rounded-lg">
                <Icon name="Tag" size={20} color="#EC4899" />
              </div>
              <h2 className="font-heading font-semibold text-xl text-text-primary">
                Vendor Categories
              </h2>
            </div>
            
            <p className="font-body text-text-secondary mb-6">
              Browse vendors by specific categories to find exactly what you need for your wedding.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {categoryPages?.map((category) => (
                <Link
                  key={category?.path}
                  to={category?.path}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-secondary-200 hover:bg-secondary-50 transition-all duration-200 group"
                >
                  <span className="font-body text-sm text-text-primary group-hover:text-secondary transition-colors duration-200">
                    {category?.label}
                  </span>
                  <Icon 
                    name="ArrowRight" 
                    size={14} 
                    className="text-text-secondary group-hover:text-secondary transition-colors duration-200" 
                  />
                </Link>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-primary to-primary-600 rounded-2xl p-6 text-white text-center">
              <Icon name="Globe" size={32} className="mx-auto mb-3 opacity-80" />
              <p className="font-heading font-bold text-2xl mb-1">25+</p>
              <p className="font-body text-sm opacity-90">Total Pages</p>
            </div>
            <div className="bg-gradient-to-br from-secondary to-secondary-600 rounded-2xl p-6 text-white text-center">
              <Icon name="Users" size={32} className="mx-auto mb-3 opacity-80" />
              <p className="font-heading font-bold text-2xl mb-1">3</p>
              <p className="font-body text-sm opacity-90">Dashboard Types</p>
            </div>
            <div className="bg-gradient-to-br from-accent to-accent-600 rounded-2xl p-6 text-white text-center">
              <Icon name="Heart" size={32} className="mx-auto mb-3 opacity-80" />
              <p className="font-heading font-bold text-2xl mb-1">8+</p>
              <p className="font-body text-sm opacity-90">Vendor Categories</p>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 text-center">
            <Icon name="MessageCircle" size={48} className="mx-auto mb-4 text-primary" />
            <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
              Can't Find What You're Looking For?
            </h3>
            <p className="font-body text-text-secondary mb-6 max-w-md mx-auto">
              Our support team is here to help you navigate ShaadiNest and find exactly what you need for your perfect wedding.
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg font-body font-medium hover:bg-primary-600 transition-colors duration-200"
            >
              <Icon name="Mail" size={18} />
              <span>Contact Support</span>
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default SiteMap;